import React, {useEffect} from 'react';
import {Box, Typography, Container, Paper} from '@mui/material';
import {Link} from 'react-router-dom';

const PrivacyPolicy = () => {
  useEffect (() => {
    // Remove background when component mounts
    document.body.style.background = 'none';
    document.body.style.backgroundColor = 'white';

    // Restore background when component unmounts
    return () => {
      document.body.style.background =
        "url('../public/bg_new.png') no-repeat center center fixed";
      document.body.style.backgroundSize = 'cover';
      document.body.style.backgroundColor = '';
    };
  }, []);

  return (
    <Box
      sx={{
        backgroundColor: '#FFFFFF',
        minHeight: '100vh',
        fontFamily: 'Arial, Ubuntu, Helvetica, sans-serif',
      }}
    >

      <Container maxWidth="lg">
        <Paper
          elevation={0}
          sx={{
            padding: {xs: 3, md: 6},
            marginBottom: 4,
            backgroundColor: '#FFFFFF',
          }}
        >
          {/* Title and Date */}
          <Typography
            variant="h2"
            sx={{
              color: '#DC143C',
              fontWeight: 'bold',
              fontSize: {xs: '2rem', md: '3rem'},
              marginBottom: 1,
              textAlign: 'center',
            }}
          >
            Privacy Policy for LLMInsight.ai
          </Typography>
          <Typography
            variant="body1"
            sx={{
              textAlign: 'center',
              marginBottom: 4,
              fontSize: '1.1rem',
              color: '#666',
            }}
          >
            Last Updated: December 25, 2024
          </Typography>

          {/* Content with Illustration */}
          <Box sx={{display: 'flex', gap: 4, flexWrap: 'wrap'}}>
            {/* Text Content */}
            <Box sx={{flex: '1', minWidth: '300px'}}>
              {/* Introduction */}
              <Typography
                variant="body1"
                sx={{
                  marginBottom: 4,
                  lineHeight: 1.6,
                  textAlign: 'justify',
                  fontSize: '1.1rem',
                }}
              >
                This Privacy Policy describes how LLMInsight.ai ("we," "us," or "our") collects, uses, and discloses your information when you use our LLMInsight.ai product and services, including our LinkedIn lead generation advertising activities. By using our services, you agree to the collection and use of information in accordance with this policy.
              </Typography>

              {/* Information We Collect */}
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 'bold',
                  color: '#002367',
                  marginBottom: 2,
                  marginTop: 4,
                }}
              >
                1. Information We Collect
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  marginBottom: 2,
                  lineHeight: 1.6,
                }}
              >
                We collect various types of information in connection with the services we provide, including:
              </Typography>
              <Box component="ul" sx={{paddingLeft: 3, marginBottom: 3}}>
                <Typography
                  component="li"
                  sx={{marginBottom: 1, lineHeight: 1.6}}
                >
                  <strong>Personal Information:</strong>
                  {' '}
                  This may include your name, email address, phone number, company name, job title, and other contact details that you provide to us directly or that we obtain through LinkedIn Lead Gen Forms.
                </Typography>
                <Typography
                  component="li"
                  sx={{marginBottom: 1, lineHeight: 1.6}}
                >
                  <strong>Usage Data:</strong>
                  {' '}
                  Information about how you access and use our services, such as your IP address, browser type, operating system, referral URLs, pages viewed, and the dates/times of your visits.
                </Typography>
                <Typography
                  component="li"
                  sx={{marginBottom: 1, lineHeight: 1.6}}
                >
                  <strong>AI Interaction Data:</strong>
                  {' '}
                  Data related to your interactions with our AI product, including queries, inputs, and outputs, which may be used to improve our services.
                </Typography>
              </Box>

              {/* How We Use Your Information */}
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 'bold',
                  color: '#002367',
                  marginBottom: 2,
                  marginTop: 4,
                }}
              >
                2. How We Use Your Information
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  marginBottom: 2,
                  lineHeight: 1.6,
                }}
              >
                We use the collected information for various purposes, including:
              </Typography>
              <Box component="ul" sx={{paddingLeft: 3, marginBottom: 3}}>
                <Typography
                  component="li"
                  sx={{marginBottom: 1, lineHeight: 1.6}}
                >
                  To provide and maintain our services, including the LLMInsight.ai product.
                </Typography>
                <Typography
                  component="li"
                  sx={{marginBottom: 1, lineHeight: 1.6}}
                >
                  To respond to your inquiries and provide customer support.
                </Typography>
                <Typography
                  component="li"
                  sx={{marginBottom: 1, lineHeight: 1.6}}
                >
                  To send you marketing and promotional communications related to our services, where you have consented to receive them.
                </Typography>
                <Typography
                  component="li"
                  sx={{marginBottom: 1, lineHeight: 1.6}}
                >
                  To analyze and improve our services, including the functionality and performance of our AI.
                </Typography>
                <Typography
                  component="li"
                  sx={{marginBottom: 1, lineHeight: 1.6}}
                >
                  To comply with legal obligations and protect our rights.
                </Typography>
                <Typography
                  component="li"
                  sx={{marginBottom: 1, lineHeight: 1.6}}
                >
                  For LinkedIn lead generation advertising, to process and manage leads obtained through LinkedIn Lead Gen Forms.
                </Typography>
              </Box>
            </Box>

            {/* Illustration */}
            <Box
              sx={{
                flex: '0 0 300px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'flex-start',
                marginTop: 2,
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  width: '250px',
                  height: '200px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                {/* Globe */}
                <Box
                  sx={{
                    width: '120px',
                    height: '120px',
                    borderRadius: '50%',
                    backgroundColor: '#87CEEB',
                    position: 'absolute',
                    zIndex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      width: '100%',
                      height: '100%',
                      borderRadius: '50%',
                      background: `
                        radial-gradient(circle at 30% 30%, rgba(255,255,255,0.3) 0%, transparent 50%),
                        linear-gradient(45deg, transparent 40%, rgba(255,255,255,0.2) 50%, transparent 60%),
                        linear-gradient(-45deg, transparent 40%, rgba(255,255,255,0.2) 50%, transparent 60%)
                      `,
                    },
                  }}
                />

                {/* Document */}
                <Box
                  sx={{
                    width: '80px',
                    height: '100px',
                    backgroundColor: '#FFB6C1',
                    position: 'absolute',
                    left: '60px',
                    top: '20px',
                    zIndex: 3,
                    borderRadius: '4px',
                    display: 'flex',
                    flexDirection: 'column',
                    padding: 1,
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: '10px',
                      left: '8px',
                      right: '8px',
                      height: '1px',
                      backgroundColor: '#333',
                    },
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      top: '15px',
                      left: '8px',
                      right: '8px',
                      height: '1px',
                      backgroundColor: '#333',
                    },
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: '8px',
                      left: '8px',
                      right: '8px',
                      height: '1px',
                      backgroundColor: '#333',
                    }}
                  />
                </Box>

                {/* Shield */}
                <Box
                  sx={{
                    width: '40px',
                    height: '50px',
                    backgroundColor: '#4169E1',
                    position: 'absolute',
                    right: '20px',
                    top: '30px',
                    zIndex: 2,
                    clipPath: 'polygon(50% 0%, 0% 25%, 0% 75%, 50% 100%, 100% 75%, 100% 25%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    '&::before': {
                      content: '""',
                      width: '12px',
                      height: '12px',
                      backgroundColor: 'white',
                      borderRadius: '50%',
                    },
                  }}
                />
              </Box>
            </Box>
          </Box>

          {/* How We Share Your Information */}
          <Typography
            variant="h5"
            sx={{
              fontWeight: 'bold',
              color: '#002367',
              marginBottom: 2,
              marginTop: 4,
            }}
          >
            3. How We Share Your Information
          </Typography>
          <Typography
            variant="body1"
            sx={{
              marginBottom: 2,
              lineHeight: 1.6,
            }}
          >
            We may share your information with third parties in the following situations:
          </Typography>
          <Box component="ul" sx={{paddingLeft: 3, marginBottom: 3}}>
            <Typography component="li" sx={{marginBottom: 1, lineHeight: 1.6}}>
              <strong>Service Providers:</strong>
              {' '}
              We may share your information with third-party service providers who perform services on our behalf, such as hosting, data analysis, marketing, and customer support.
            </Typography>
            <Typography component="li" sx={{marginBottom: 1, lineHeight: 1.6}}>
              <strong>Business Transfers:</strong>
              {' '}
              In the event of a merger, acquisition, or sale of all or a portion of our assets, your information may be transferred as part of that transaction.
            </Typography>
            <Typography component="li" sx={{marginBottom: 1, lineHeight: 1.6}}>
              <strong>Legal Requirements:</strong>
              {' '}
              We may disclose your information if required to do so by law or in response to valid requests by public authorities (e.g., a court or government agency).
            </Typography>
            <Typography component="li" sx={{marginBottom: 1, lineHeight: 1.6}}>
              <strong>With Your Consent:</strong>
              {' '}
              We may share your information with your consent or at your direction.
            </Typography>
          </Box>

          {/* Data Security */}
          <Typography
            variant="h5"
            sx={{
              fontWeight: 'bold',
              color: '#002367',
              marginBottom: 2,
              marginTop: 4,
            }}
          >
            4. Data Security
          </Typography>
          <Typography
            variant="body1"
            sx={{
              marginBottom: 3,
              lineHeight: 1.6,
            }}
          >
            We implement reasonable security measures to protect your personal information from unauthorized access, use, or disclosure. However, no method of transmission over the Internet or method of electronic storage is 100% secure.
          </Typography>

          {/* Your Data Protection Rights */}
          <Typography
            variant="h5"
            sx={{
              fontWeight: 'bold',
              color: '#002367',
              marginBottom: 2,
              marginTop: 4,
            }}
          >
            5. Your Data Protection Rights
          </Typography>
          <Typography
            variant="body1"
            sx={{
              marginBottom: 2,
              lineHeight: 1.6,
            }}
          >
            Depending on your location and applicable laws (e.g., GDPR, CCPA/CPRA), you may have certain rights regarding your personal data, including:
          </Typography>
          <Box component="ul" sx={{paddingLeft: 3, marginBottom: 3}}>
            <Typography component="li" sx={{marginBottom: 1, lineHeight: 1.6}}>
              The right to access, update, or delete the information we have on you.
            </Typography>
            <Typography component="li" sx={{marginBottom: 1, lineHeight: 1.6}}>
              The right to object to our processing of your personal data.
            </Typography>
            <Typography component="li" sx={{marginBottom: 1, lineHeight: 1.6}}>
              The right to request that we restrict the processing of your personal data.
            </Typography>
            <Typography component="li" sx={{marginBottom: 1, lineHeight: 1.6}}>
              The right to data portability.
            </Typography>
            <Typography component="li" sx={{marginBottom: 1, lineHeight: 1.6}}>
              The right to withdraw consent at any time where we rely on your consent to process your personal information.
            </Typography>
          </Box>
          <Typography
            variant="body1"
            sx={{
              marginBottom: 3,
              lineHeight: 1.6,
              fontStyle: 'italic',
            }}
          >
            To exercise any of these rights, please contact us using the contact details provided below.
          </Typography>

          {/* Third-Party Links */}
          <Typography
            variant="h5"
            sx={{
              fontWeight: 'bold',
              color: '#002367',
              marginBottom: 2,
              marginTop: 4,
            }}
          >
            6. Third-Party Links
          </Typography>
          <Typography
            variant="body1"
            sx={{
              marginBottom: 3,
              lineHeight: 1.6,
            }}
          >
            Our services may contain links to third-party websites or services that are not operated by us. We have no control over and assume no responsibility for the content, privacy policies, or practices of any third-party sites or services.
          </Typography>

          {/* Changes to This Privacy Policy */}
          <Typography
            variant="h5"
            sx={{
              fontWeight: 'bold',
              color: '#002367',
              marginBottom: 2,
              marginTop: 4,
            }}
          >
            7. Changes to This Privacy Policy
          </Typography>
          <Typography
            variant="body1"
            sx={{
              marginBottom: 3,
              lineHeight: 1.6,
            }}
          >
            We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date. You are advised to review this Privacy Policy periodically for any changes.
          </Typography>

          {/* Contact Us */}

        </Paper>
      </Container>

    </Box>
  );
};

export default PrivacyPolicy;
